import React, { useState, useEffect } from 'react';
import { 
  MessageCircle, 
  Check, 
  Star, 
  Mail,
  Zap,
  Shield,
  Building,
  X,
  AlertCircle,
  ArrowRight
} from 'lucide-react';

const Homepage = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');

  useEffect(() => {
    // Google Analytics
    window.dataLayer = window.dataLayer || [];
    function gtag(){window.dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', 'G-V8ESDMZ5NZ');

    // SEO-OPTIMIZED: Updated title and meta description
    document.title = 'Customer Support Software for Small Business | TicketDesk - $29/month';
    
    // Add meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Affordable customer support ticketing system with Gmail, Facebook, WhatsApp integration + Trustpilot review alerts. Unlimited team members. Start free in 60 seconds.';

    // Set canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = 'https://www.ticketdesk.app/';

    // SEO-OPTIMIZED: Enhanced structured data with more details
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "TicketDesk",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "offers": {
        "@type": "AggregateOffer",
        "lowPrice": "0",
        "highPrice": "149",
        "priceCurrency": "USD",
        "priceValidUntil": "2025-12-31"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "ratingCount": "500",
        "bestRating": "5",
        "worstRating": "1"
      },
      "description": "Customer support ticketing system for small businesses with Gmail, Facebook, WhatsApp and website chat widget integration, plus Trustpilot & Capterra review monitoring. AI-assisted responses. Unlimited team members.",
      "featureList": [
        "Gmail integration",
        "Facebook Messenger support",
        "WhatsApp Business integration",
        "Trustpilot review alerts",
        "Capterra review monitoring",
        "AI-powered response polish",
        "Unlimited team members",
        "Website chat widget"
      ],
      "screenshot": "https://www.ticketdesk.app/screenshot.png",
      "softwareVersion": "1.0",
      "provider": {
        "@type": "Organization",
        "name": "TicketDesk"
      }
    };

    let script = document.querySelector('script[type="application/ld+json"]');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(structuredData);

    // SEO-OPTIMIZED: Add FAQ Schema
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How much does TicketDesk cost?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "TicketDesk starts at $0 for the free plan (10 tickets/month). Paid plans start at $29/month for 200 tickets with unlimited team members included."
          }
        },
        {
          "@type": "Question",
          "name": "Can I add unlimited team members?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes! All TicketDesk plans include unlimited team members at no extra cost. Unlike Zendesk or Freshdesk, we don't charge per agent."
          }
        },
        {
          "@type": "Question",
          "name": "What integrations does TicketDesk support?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "TicketDesk integrates with Gmail, Facebook Messenger, WhatsApp Business, Trustpilot, and Capterra. We also provide a website chat widget."
          }
        },
        {
          "@type": "Question",
          "name": "How long does setup take?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Setup takes just 60 seconds. Simply connect your Gmail account and you're ready to start managing customer support tickets."
          }
        }
      ]
    };

    let faqScript = document.querySelector('script[data-schema="faq"]');
    if (!faqScript) {
      faqScript = document.createElement('script');
      faqScript.type = 'application/ld+json';
      faqScript.setAttribute('data-schema', 'faq');
      document.head.appendChild(faqScript);
    }
    faqScript.textContent = JSON.stringify(faqSchema);

  }, []);

  const trackEvent = (eventName, eventParams = {}) => {
    if (window.gtag) {
      window.gtag('event', eventName, eventParams);
    }
  };

  const handleStartFreeClick = (location) => {
    trackEvent('start_free_click', {
      button_location: location,
      button_text: 'Start Free',
      value: 1
    });
  };

  const handleGetStartedClick = (plan, price) => {
    trackEvent('get_started_click', {
      plan_name: plan,
      plan_price: price,
      button_location: 'pricing_section',
      value: 1
    });
  };

  const handleCreateAccountClick = (location) => {
    trackEvent('create_account_click', {
      button_location: location,
      button_text: 'Create Account',
      value: 1
    });
  };

  const handleBookDemoClick = (location) => {
    trackEvent('book_demo_click', {
      button_location: location,
      button_text: 'Book a Demo',
      value: 1
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center min-w-0">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-message-circle">
                  <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719"></path>
                </svg>
              </div>
              <span className="ml-2 sm:ml-3 text-lg sm:text-xl font-semibold text-gray-900">TicketDesk</span>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4 lg:space-x-8">
              <a href="/blog" className="text-xs sm:text-sm text-gray-700 hover:text-gray-900 hidden md:block">Blog</a>
              <a href="#features" className="text-xs sm:text-sm text-gray-700 hover:text-gray-900 hidden md:block">Features</a>
              <a href="#pricing" className="text-xs sm:text-sm text-gray-700 hover:text-gray-900">Pricing</a>
              <a href="/login" className="text-xs sm:text-sm text-gray-700 hover:text-gray-900 hidden lg:block">Sign In</a>
              <a href="/register" onClick={() => handleCreateAccountClick('navigation')} className="bg-purple-600 text-white px-3 sm:px-4 lg:px-5 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium hover:bg-purple-700 whitespace-nowrap">
                Try free
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - SEO OPTIMIZED */}
      <section className="py-12 sm:py-16 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left: Content */}
            <div>
              {/* SEO-OPTIMIZED: H1 with primary keyword */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
                Customer Support Software for Small Business
              </h1>
              {/* SEO-OPTIMIZED: Enhanced description with keywords */}
              <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                Affordable help desk ticketing system with Gmail, Facebook, WhatsApp integration and the 'Ask me a question' website widget. Monitor Trustpilot & Capterra reviews. AI helps you sound professional. Unlimited team members included.
              </p>
              
              <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 mb-6 sm:mb-8">
                <a 
                  href="/register" 
                  onClick={() => handleStartFreeClick('hero_section')}
                  className="inline-flex items-center justify-center bg-purple-600 text-white px-5 sm:px-6 py-3 rounded text-sm sm:text-base font-medium hover:bg-purple-700 text-center"
                >
                  Start Free
                </a>
                <a
                  href="https://calendly.com/hello-ticketdesk/demo"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleBookDemoClick('hero_section')}
                  className="inline-flex items-center justify-center border border-gray-300 text-gray-700 px-5 sm:px-6 py-3 rounded text-sm sm:text-base font-medium hover:border-gray-400 text-center"
                >
                  Book a Demo
                </a>
              </div>
              
              <p className="text-xs sm:text-sm text-gray-500">No credit card required • Setup in 60 seconds</p>
            </div>

            {/* Right: Visual */}
            <div className="mt-8 lg:mt-0">
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 shadow-lg">
                <div className="bg-white rounded-xl shadow-md p-6 mb-4">
                  <div className="flex items-center mb-4">
                    <Mail className="w-5 h-5 text-blue-600 mr-3" />
                    <div className="flex-1">
                      <div className="font-semibold text-sm">Order question - Gmail</div>
                      <div className="text-xs text-gray-500">Sarah J. • 5 min ago</div>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-md p-6 mb-4">
                  <div className="flex items-center mb-4">
                    <MessageCircle className="w-5 h-5 text-blue-500 mr-3" />
                    <div className="flex-1">
                      <div className="font-semibold text-sm">Product inquiry - Facebook</div>
                      <div className="text-xs text-gray-500">Mike W. • 12 min ago</div>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-red-500">
                  <div className="flex items-center mb-2">
                    <Star className="w-5 h-5 text-red-500 mr-3 fill-current" />
                    <div className="flex-1">
                      <div className="font-semibold text-sm text-red-700">1-Star Trustpilot Review</div>
                      <div className="text-xs text-gray-500">Lisa P. • 10 min ago</div>
                    </div>
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  </div>
                  <button className="text-xs bg-red-100 text-red-700 px-3 py-1 rounded-full font-medium mt-2">
                    Respond Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Channel Logos Section */}
      <section className="py-8 sm:py-12 bg-white border-y border-gray-100 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center">
            {/* Three Column Layout */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12 w-full mb-8">
              {/* Reviews Column */}
              <div className="flex flex-col items-center">
                <div className="text-center mb-4">
                  <p className="text-sm font-semibold text-purple-600 uppercase tracking-wide">Review Monitoring</p>
                </div>
                <div className="flex items-center justify-center gap-3 sm:gap-4 flex-wrap">
                  {/* Trustpilot Logo */}
                  <div className="flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-lg border border-gray-200 shadow-sm flex-shrink-0">
                    <svg viewBox="0 0 126 30" className="w-14 h-9 sm:w-16 sm:h-10" aria-label="Trustpilot">
                      <path fill="#00B67A" d="M30.4 11.4L20.8 0 11.2 11.4H0l20.8 18.2L41.6 11.4z"/>
                      <path fill="#005128" d="M23.4 19.8l-2.6-8.4h8.6z"/>
                      <text x="45" y="22" fill="#191919" style={{fontSize: '18px', fontWeight: 'bold', fontFamily: 'Arial, sans-serif'}}>Trustpilot</text>
                    </svg>
                  </div>
                  
                  <span className="text-2xl sm:text-3xl text-gray-400 font-light flex-shrink-0">+</span>
                  
                  {/* Capterra Logo */}
                  <div className="flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-lg border border-gray-200 shadow-sm flex-shrink-0">
                    <svg viewBox="0 0 120 30" className="w-14 h-9 sm:w-16 sm:h-10" aria-label="Capterra">
                      <circle cx="15" cy="15" r="12" fill="#FF6D42"/>
                      <path d="M15 7 L19 15 L15 23 L11 15 Z" fill="white"/>
                      <text x="32" y="21" fill="#2C3E50" style={{fontSize: '16px', fontWeight: 'bold', fontFamily: 'Arial, sans-serif'}}>Capterra</text>
                    </svg>
                  </div>
                </div>
              </div>

              {/* Ticketing Column */}
              <div className="flex flex-col items-center">
                <div className="text-center mb-4">
                  <p className="text-sm font-semibold text-blue-600 uppercase tracking-wide">Ticketing Channels</p>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:gap-4 w-full max-w-xs">
                  {/* Gmail Logo */}
                  <div className="flex items-center justify-center w-full h-16 sm:h-20 bg-white rounded-lg border border-gray-200 shadow-sm">
                    <svg viewBox="0 0 256 193" className="w-8 h-8 sm:w-10 sm:h-10" aria-label="Gmail">
                      <path fill="#4285F4" d="M58.182 192.05V93.14L27.507 65.077 0 49.504v125.091c0 9.658 7.825 17.455 17.455 17.455h40.727z"/>
                      <path fill="#34A853" d="M197.818 192.05h40.727c9.659 0 17.455-7.826 17.455-17.455V49.505l-31.156 17.837-27.026 25.798v98.91z"/>
                      <path fill="#EA4335" d="M58.182 93.14l-4.174-38.647 4.174-36.989L128 69.868l69.818-52.364 4.669 34.992-4.669 40.644L128 145.504z"/>
                      <path fill="#FBBC04" d="M197.818 17.504V93.14L256 49.504V26.231c0-21.585-24.64-33.89-41.89-20.945l-16.292 12.218z"/>
                      <path fill="#C5221F" d="M0 49.504l26.759 20.07L58.182 93.14V17.504L41.89 5.286C24.61-7.66 0 4.646 0 26.23v23.274z"/>
                    </svg>
                  </div>
                  
                  {/* Facebook Logo */}
                  <div className="flex items-center justify-center w-full h-16 sm:h-20 bg-white rounded-lg border border-gray-200 shadow-sm">
                    <svg viewBox="0 0 256 256" className="w-8 h-8 sm:w-10 sm:h-10" aria-label="Facebook">
                      <path fill="#1877F2" d="M256 128C256 57.308 198.692 0 128 0C57.308 0 0 57.307 0 128c0 63.888 46.808 116.843 108 126.445V165H75.5v-37H108V99.8c0-32.08 19.11-49.8 48.348-49.8C170.352 50 185 52.5 185 52.5V84h-16.14C152.959 84 148 93.867 148 103.99V128h35.5l-5.675 37H148v89.445c61.192-9.602 108-62.556 108-126.445"/>
                      <path fill="#FFF" d="M177.825 165L183.5 128H148v-24.01C148 93.866 152.959 84 168.86 84H185V52.5S170.352 50 156.347 50C127.11 50 108 67.72 108 99.8V128H75.5v37H108v89.445A128.959 128.959 0 0 0 128 256a128.9 128.9 0 0 0 20-1.555V165h29.825"/>
                    </svg>
                  </div>

                  {/* WhatsApp Logo */}
                  <div className="flex items-center justify-center w-full h-16 sm:h-20 bg-white rounded-lg border border-gray-200 shadow-sm">
                    <svg viewBox="0 0 175.216 175.552" className="w-10 h-10 sm:w-12 sm:h-12" aria-label="WhatsApp">
                      <defs>
                        <linearGradient id="whatsapp-gradient" x1="85.915" x2="86.535" y1="32.567" y2="137.092" gradientUnits="userSpaceOnUse">
                          <stop offset="0" stopColor="#57d163"/>
                          <stop offset="1" stopColor="#23b33a"/>
                        </linearGradient>
                      </defs>
                      <path fill="url(#whatsapp-gradient)" d="M87.184 25.227c-33.733 0-61.166 27.423-61.178 61.13a60.98 60.98 0 0 0 9.349 32.535l1.455 2.312-6.179 22.559 23.146-6.069 2.235 1.324c9.387 5.571 20.15 8.518 31.126 8.524h.023c33.707 0 61.14-27.426 61.153-61.135a60.75 60.75 0 0 0-17.895-43.251 60.75 60.75 0 0 0-43.235-17.929z"/>
                      <path fill="#FFF" d="M68.772 55.603c-1.378-3.061-2.828-3.123-4.137-3.176l-3.524-.043c-1.226 0-3.218.46-4.902 2.3s-6.435 6.287-6.435 15.332 6.588 17.785 7.506 19.013 12.718 20.381 31.405 27.75c15.529 6.124 18.689 4.906 22.061 4.6s10.877-4.447 12.408-8.74 1.532-7.971 1.073-8.74-1.685-1.226-3.525-2.146-10.877-5.367-12.562-5.981-2.91-.919-4.137.921-4.746 5.979-5.819 7.206-2.144 1.381-3.984.462-7.76-2.861-14.784-9.124c-5.465-4.873-9.154-10.891-10.228-12.73s-.114-2.835.808-3.751c.825-.824 1.838-2.147 2.759-3.22s1.224-1.84 1.836-3.065.307-2.301-.153-3.22-4.032-10.011-5.666-13.647"/>
                    </svg>
                  </div>
                  
                  {/* Website Widget Logo */}
                  <div className="flex items-center justify-center w-full h-16 sm:h-20 bg-white rounded-lg border border-gray-200 shadow-sm">
                    <svg viewBox="0 0 24 24" className="w-10 h-10 sm:w-12 sm:h-12" fill="none" aria-label="Website Widget">
                      <rect x="2" y="2" width="20" height="20" rx="3" fill="#F3F4F6" stroke="#D1D5DB" strokeWidth="1.5"/>
                      <rect x="2" y="2" width="20" height="5" rx="3" fill="#9333EA"/>
                      <circle cx="5" cy="4.5" r="0.8" fill="white"/>
                      <circle cx="7.5" cy="4.5" r="0.8" fill="white"/>
                      <circle cx="10" cy="4.5" r="0.8" fill="white"/>
                      <circle cx="17" cy="17" r="4.5" fill="#9333EA"/>
                      <path d="M15 17h4M17 15v4" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </div>
                </div>
              </div>

              {/* TicketDesk Column */}
              <div className="flex flex-col items-center">
                <div className="text-center mb-4">
                  <p className="text-sm font-semibold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent uppercase tracking-wide">TicketDesk</p>
                </div>
                <div className="flex items-center justify-center">
                  {/* TicketDesk Logo */}
                  <div className="flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg shadow-lg flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sm:w-10 sm:h-10">
                      <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719"></path>
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center px-4">
              <p className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 mb-2">Gmail, Facebook Messenger, WhatsApp Business ticketing with 'Ask me a question' website widget.<br/> Trustpilot & Capterra review monitoring included</p>
              <p className="text-sm sm:text-base text-gray-600">Connect all channels in under 3 minutes</p>
            </div>
          </div>
        </div>
      </section>

      {/* SEO-OPTIMIZED Stats Bar with keywords */}
      <section className="py-12 bg-gray-50 border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-purple-600 mb-2">500+</div>
              <div className="text-sm text-gray-600">Small businesses using TicketDesk</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-purple-600 mb-2">$29</div>
              <div className="text-sm text-gray-600">Affordable starting price per month</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-purple-600 mb-2">60 sec</div>
              <div className="text-sm text-gray-600">Quick setup time</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-purple-600 mb-2">Unlimited</div>
              <div className="text-sm text-gray-600">Team members included free</div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO-OPTIMIZED: Added H2 with secondary keywords */}
      {/* Value Prop 1 */}
      <section id="features" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Help Desk Ticketing System for Small Business
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Affordable customer support software that handles every question, request, and issue. Gmail, Facebook Messenger, WhatsApp Business, and website chat messages become trackable tickets your team can manage together.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <Check className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Gmail ticketing system in 60 seconds</div>
                    <div className="text-gray-600">Connect your email inbox and start tracking customer support</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <Check className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Facebook, WhatsApp & website chat widget</div>
                    <div className="text-gray-600">Multi-channel support - never miss a message from any channel</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <Check className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Assign tickets to unlimited team members</div>
                    <div className="text-gray-600">Team collaboration and faster resolution - no per-seat pricing</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-12">
              <div className="space-y-4">
                <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-blue-500">
                  <div className="text-sm font-semibold text-gray-900 mb-2">All messages in one unified inbox</div>
                  <div className="text-xs text-gray-600">Gmail, Facebook, WhatsApp, website chat</div>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="text-sm font-semibold text-gray-900 mb-2">Assign and prioritize support requests</div>
                  <div className="text-xs text-gray-600">Team collaboration made easy</div>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="text-sm font-semibold text-gray-900 mb-2">Track everything automatically</div>
                  <div className="text-xs text-gray-600">Response times and resolution metrics</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Prop 2 */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-12">
              <div className="space-y-4">
                <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-red-500">
                  <div className="flex items-center mb-2">
                    <Star className="w-5 h-5 text-red-500 fill-current mr-2" />
                    <div className="text-sm font-semibold text-red-700">1-Star Alert</div>
                  </div>
                  <div className="text-xs text-gray-600">Instant notification sent</div>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-yellow-500">
                  <div className="flex items-center mb-2">
                    <Star className="w-5 h-5 text-yellow-500 fill-current mr-2" />
                    <Star className="w-5 h-5 text-yellow-500 fill-current mr-2" />
                    <div className="text-sm font-semibold text-yellow-700">2-Star Alert</div>
                  </div>
                  <div className="text-xs text-gray-600">Review needs response</div>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="text-sm font-semibold text-gray-900 mb-2">Respond in minutes, not days</div>
                  <div className="text-xs text-gray-600">Protect your reputation in real-time</div>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Trustpilot & Capterra Review Management
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Protect your online reputation in real-time. Get instant alerts for Trustpilot and Capterra reviews under 3 stars and respond before damage is done.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <Check className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Trustpilot & Capterra review alerts</div>
                    <div className="text-gray-600">Know immediately when reviews appear on review platforms</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <Check className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Alert for negative reviews under 3 stars</div>
                    <div className="text-gray-600">Focus on what needs your attention for reputation management</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <Check className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Stop reputation damage fast</div>
                    <div className="text-gray-600">Respond within hours, not days - review monitoring included</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Prop 3 */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                AI-Powered Customer Support Responses
              </h2>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Makes your customer service replies sound professional. Not chatbot responses - your words, better written. Save time while maintaining quality with AI assistance.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start">
                  <Check className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Fixes grammar and tone instantly</div>
                    <div className="text-gray-600">Sound professional every time with AI polish</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <Check className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Your words, better written</div>
                    <div className="text-gray-600">Not generic templates or bot responses - authentic AI help</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <Check className="w-6 h-6 text-green-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900 mb-1">Save hours every week on support</div>
                    <div className="text-gray-600">Write faster without sacrificing quality</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-12">
              <div className="bg-white p-6 rounded-xl shadow-sm mb-4">
                <div className="text-xs text-gray-500 mb-2">Your quick reply:</div>
                <div className="text-sm text-gray-700 mb-4">"sorry for delay will ship tomorrow"</div>
                <div className="border-t pt-4">
                  <div className="text-xs text-purple-600 font-medium mb-2">AI polished:</div>
                  <div className="text-sm text-gray-900">"Thank you for your patience. I apologize for the delay. Your order will ship tomorrow and you'll receive tracking information shortly."</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Affordable Help Desk Pricing - Unlimited Users
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              No per-seat fees. Add your whole team. Scale your small business as you grow.
            </p>
            
            <div className="flex items-center justify-center">
              <div className="bg-white rounded-lg p-1 shadow-md border border-gray-200">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-6 py-2 rounded-md font-medium transition-all ${
                    billingCycle === 'monthly'
                      ? 'bg-purple-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle('yearly')}
                  className={`px-6 py-2 rounded-md font-medium transition-all ${
                    billingCycle === 'yearly'
                      ? 'bg-purple-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Yearly (Save up to $358)
                </button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {/* Free Plan */}
            <div className="bg-gray-50 rounded-xl p-6 border-2 border-gray-200">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Free</h3>
                <div className="text-3xl font-bold text-gray-900 mb-2">$0</div>
                <div className="text-gray-600 text-sm">forever</div>
                <div className="mt-2 text-sm text-gray-500">10 tickets/month</div>
              </div>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">10 tickets per month</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-500">10 reviews per month</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Unlimited team members</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">1 workspace</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Full Integration Package</span>
                </div>
              </div>

              <a 
                href="/register"
                onClick={() => handleGetStartedClick('Free', '0')}
                className="w-full bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-700 transition-colors text-center block"
              >
                Get Started
              </a>
            </div>

            {/* Starter Plan */}
            <div className="bg-white rounded-xl p-6 border-2 border-purple-500 relative shadow-lg">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-purple-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                  Most Popular
                </span>
              </div>
              
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Starter</h3>
                <div className="text-3xl font-bold text-purple-600 mb-2">
                  ${billingCycle === 'yearly' ? '23' : '29'}
                </div>
                <div className="text-gray-600 text-sm">per month</div>
                {billingCycle === 'yearly' && (
                  <div className="text-xs text-green-600 font-medium mt-1">Save $70/year</div>
                )}
                <div className="mt-2 text-sm text-purple-600 font-medium">200 tickets/month</div>
              </div>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">200 tickets monthly</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Unlimited review scanning</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Unlimited team members</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">1 workspace</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Full Integration Package</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">AI powered responses</span>
                </div>
              </div>

              <a 
                href="/register"
                onClick={() => handleGetStartedClick('Starter', billingCycle === 'yearly' ? '23' : '29')}
                className="w-full bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-purple-700 transition-colors text-center block"
              >
                Get Started
              </a>
            </div>

            {/* Growth Plan */}
            <div className="bg-gray-50 rounded-xl p-6 border-2 border-gray-200">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Growth</h3>
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  ${billingCycle === 'yearly' ? '63' : '79'}
                </div>
                <div className="text-gray-600 text-sm">per month</div>
                {billingCycle === 'yearly' && (
                  <div className="text-xs text-green-600 font-medium mt-1">Save $190/year</div>
                )}
                <div className="mt-2 text-sm text-gray-600">1,000 tickets/month</div>
              </div>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">1,000 tickets monthly</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Unlimited review scanning</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Unlimited team members</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">5 workspaces</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Full Integration Package</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">AI powered responses</span>
                </div>
              </div>

              <a 
                href="/register"
                onClick={() => handleGetStartedClick('Growth', billingCycle === 'yearly' ? '63' : '79')}
                className="w-full bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-700 transition-colors text-center block"
              >
                Get Started
              </a>
            </div>

            {/* Scale Plan */}
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6 border-2 border-purple-300 relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center">
                  <Building className="w-3 h-3 mr-1" />
                  For Agencies
                </span>
              </div>
              
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Scale</h3>
                <div className="text-3xl font-bold text-purple-600 mb-2">
                  ${billingCycle === 'yearly' ? '119' : '149'}
                </div>
                <div className="text-gray-600 text-sm">per month</div>
                {billingCycle === 'yearly' && (
                  <div className="text-xs text-green-600 font-medium mt-1">Save $360/year</div>
                )}
                <div className="mt-2 text-sm text-purple-600 font-medium">5,000 tickets/month</div>
              </div>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">5,000 tickets monthly</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Unlimited review scanning</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Unlimited team members</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Unlimited workspaces</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">Full Integration Package</span>
                </div>
                <div className="flex items-start">
                  <Check className="w-4 h-4 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-gray-700">AI powered responses</span>
                </div>
              </div>

              <div className="bg-white rounded-lg p-3 mb-4 border border-purple-200">
                <div className="text-xs text-gray-600 mb-1">Managing 5 clients elsewhere:</div>
                <div className="text-sm font-semibold text-red-600 line-through">$250+/month</div>
                <div className="text-xs text-gray-600 mb-1 mt-2">Managing 50 clients with TicketDesk:</div>
                <div className="text-sm font-bold text-green-600">Still $149/month</div>
              </div>

              <a 
                href="/register"
                onClick={() => handleGetStartedClick('Scale', billingCycle === 'yearly' ? '119' : '149')}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity text-center block"
              >
                Get Started
              </a>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-sm text-gray-600 mb-4">All paid plans include a 7-day free trial with full access. No credit card required to start.</p>
            <div className="flex justify-center items-center space-x-8 text-sm text-gray-500">
              <div className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-2" />
                No setup fees
              </div>
              <div className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-2" />
                Cancel anytime
              </div>
              <div className="flex items-center">
                <Check className="w-4 h-4 text-green-500 mr-2" />
                30-day money back guarantee
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-purple-600">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Start Your Free Trial Today
          </h2>
          <p className="text-xl text-white mb-8 opacity-90">
            No credit card required • Setup in 60 seconds • Cancel anytime
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/register"
              onClick={() => handleStartFreeClick('cta_section')}
              className="inline-flex items-center justify-center bg-white text-purple-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100"
            >
              Start Free Forever
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
            <a
              href="https://calendly.com/hello-ticketdesk/demo"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleBookDemoClick('cta_section')}
              className="inline-flex items-center justify-center border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-purple-600"
            >
              Book a Demo
            </a>
          </div>
        </div>
      </section>

      {/* SEO-OPTIMIZED Footer with better internal linking */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-message-circle">
                    <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719"></path>
                  </svg>
                </div>
                <span className="ml-3 text-xl font-bold">TicketDesk</span>
              </div>
              <p className="text-gray-400 max-w-md">
                Affordable customer support software for small business. Gmail, Facebook, WhatsApp ticketing with Trustpilot review monitoring.
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Product</h3>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><a href="#features" className="hover:text-white">Features</a></li>
                <li><a href="#pricing" className="hover:text-white">Pricing</a></li>
                <li><a href="/integrations" className="hover:text-white">Integrations</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Solutions</h3>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><a href="/for-ecommerce" className="hover:text-white">For E-commerce Shops</a></li>
                <li><a href="/for-saas" className="hover:text-white">For SaaS Companies</a></li>
                <li><a href="/for-agencies" className="hover:text-white">For Agencies</a></li>
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2"></div>

            <div>
              <h3 className="font-semibold mb-4">Compare</h3>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><a href="/compare/zendesk" className="hover:text-white">vs Zendesk</a></li>
                <li><a href="/compare/freshdesk" className="hover:text-white">vs Freshdesk</a></li>
                <li><a href="/compare/email" className="hover:text-white">vs Email</a></li>
                <li><a href="/pricing-comparison" className="hover:text-white">Pricing Comparison</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; 2025 TicketDesk. Affordable customer support ticketing system for small business. Gmail, Facebook, WhatsApp & website chat widget. Trustpilot & Capterra review monitoring. Unlimited team members. No per-seat pricing.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Homepage;